import { FormEvent, useEffect, useState } from "react";
import { Plus } from "lucide-react";

import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveSetupTabs } from "../components/LeaveSetupTabs";
import { useAsyncData, useMutation } from "../hooks";
import type {
  LeaveYearPolicy,
  LeaveYearPolicyCreateRequest,
} from "../types";

function yearPolicy(year: number): LeaveYearPolicyCreateRequest {
  return {
    leaveYear: year,
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
    active: true,
    closed: false,
  };
}

export default function LeaveYearPoliciesPage() {
  const [editing, setEditing] = useState<LeaveYearPolicy | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [prefill, setPrefill] = useState<LeaveYearPolicyCreateRequest | null>(null);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.leaveYearPolicies(),
    [],
  );

  const save = useMutation(async (payload: LeaveYearPolicyCreateRequest) => {
    if (editing) {
      return leaveApi.updateLeaveYearPolicy(editing.id, payload);
    }

    return leaveApi.createLeaveYearPolicy(payload);
  });

  async function handleSubmit(payload: LeaveYearPolicyCreateRequest) {
    await save.mutate(payload);
    setEditing(null);
    setShowForm(false);
    setPrefill(null);
    await reload();
  }

  function openForYear(year: number) {
    setEditing(null);
    setPrefill(yearPolicy(year));
    setShowForm(true);
  }

  // Smart suggestions
  const now = new Date();
  const currentYear = now.getFullYear();
  const nextYear = currentYear + 1;
  const isQ4 = now.getMonth() >= 9; // Oct–Dec

  const existingYears = new Set((data ?? []).map((p) => p.leaveYear));
  const missingCurrent = !existingYears.has(currentYear);
  const missingNext = !existingYears.has(nextYear);

  async function toggleActive(item: LeaveYearPolicy) {
    if (item.active) {
      await leaveApi.deactivateLeaveYearPolicy(item.id);
    } else {
      await leaveApi.activateLeaveYearPolicy(item.id);
    }

    await reload();
  }

  async function toggleClosed(item: LeaveYearPolicy) {
    if (item.closed) {
      await leaveApi.reopenLeaveYearPolicy(item.id);
    } else {
      await leaveApi.closeLeaveYearPolicy(item.id);
    }

    await reload();
  }

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Leave Years"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave/setup" label="Setup Hub" />
            <button
              type="button"
              onClick={() => { setEditing(null); setPrefill(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
          >
            <Plus className="h-4 w-4" />
            New Leave Year
            </button>
          </div>
        }
      />

      <LeaveSetupTabs />

      {/* Smart setup banners */}
      {!loading && missingCurrent && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div>
            <p className="font-bold text-amber-900">Current year not configured</p>
            <p className="text-sm text-amber-700">
              Leave year {currentYear} is missing. Employees cannot apply for leave without it.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openForYear(currentYear)}
            className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700"
          >
            Set up {currentYear}
          </button>
        </div>
      )}

      {!loading && !missingCurrent && isQ4 && missingNext && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4">
          <div>
            <p className="font-bold text-blue-900">Q4 reminder — set up {nextYear}</p>
            <p className="text-sm text-blue-700">
              You're in the last quarter of {currentYear}. Create the {nextYear} leave year now
              to allow carry-forward processing and employee leave planning.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openForYear(nextYear)}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
          >
            Set up {nextYear}
          </button>
        </div>
      )}

      {error ? <LeaveError message={error} onRetry={reload} /> : null}
      {save.error ? <LeaveError message={save.error} /> : null}

      {showForm || editing ? (
        <LeaveYearPolicyForm
          initialValue={editing}
          prefill={prefill}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => {
            setEditing(null);
            setShowForm(false);
            setPrefill(null);
          }}
        />
      ) : null}

      {loading ? <LeaveLoading label="Loading leave years..." /> : null}

      {data ? (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Year</th>
                  <th className="px-4 py-3">Start Date</th>
                  <th className="px-4 py-3">End Date</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3">Closed</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {data.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-black">{item.leaveYear}</td>
                    <td className="px-4 py-3">{item.startDate}</td>
                    <td className="px-4 py-3">{item.endDate}</td>
                    <td className="px-4 py-3">
                      <Status
                        active={item.active}
                        trueText="Active"
                        falseText="Inactive"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Status
                        active={item.closed}
                        trueText="Closed"
                        falseText="Open"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(item);
                            setShowForm(true);
                          }}
                          className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleActive(item)}
                          className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                        >
                          {item.active ? "Deactivate" : "Activate"}
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleClosed(item)}
                          className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                        >
                          {item.closed ? "Reopen" : "Close"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!data.length ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-10 text-center text-slate-500"
                    >
                      No leave year policy configured.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function LeaveYearPolicyForm({
  initialValue,
  prefill,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: LeaveYearPolicy | null;
  prefill?: LeaveYearPolicyCreateRequest | null;
  submitting?: boolean;
  onSubmit: (payload: LeaveYearPolicyCreateRequest) => Promise<void> | void;
  onCancel: () => void;
}) {
  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState<LeaveYearPolicyCreateRequest>(
    prefill ?? {
      leaveYear: currentYear,
      startDate: `${currentYear}-01-01`,
      endDate: `${currentYear}-12-31`,
      active: true,
      closed: false,
    }
  );

  useEffect(() => {
    if (initialValue) {
      setForm({
        leaveYear: initialValue.leaveYear,
        startDate: initialValue.startDate,
        endDate: initialValue.endDate,
        active: initialValue.active,
        closed: initialValue.closed,
      });
    } else if (prefill) {
      setForm(prefill);
    }
  }, [initialValue, prefill]);

  async function submit(event: FormEvent) {
    event.preventDefault();

    await onSubmit({
      leaveYear: Number(form.leaveYear),
      startDate: form.startDate,
      endDate: form.endDate,
      active: Boolean(form.active),
      closed: Boolean(form.closed),
    });
  }

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-3xl border bg-white p-5 shadow-sm md:grid-cols-3"
    >
      <Field label="Leave Year" required>
        <input
          type="number"
          className="input"
          min={2000}
          max={2100}
          value={form.leaveYear}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              leaveYear: Number(event.target.value),
            }))
          }
          required
        />
      </Field>

      <Field label="Start Date" required>
        <input
          type="date"
          className="input"
          value={form.startDate}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              startDate: event.target.value,
            }))
          }
          required
        />
      </Field>

      <Field label="End Date" required>
        <input
          type="date"
          className="input"
          value={form.endDate}
          min={form.startDate}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              endDate: event.target.value,
            }))
          }
          required
        />
      </Field>

      <Check
        label="Active"
        checked={Boolean(form.active)}
        onChange={(value) =>
          setForm((previous) => ({ ...previous, active: value }))
        }
      />

      <Check
        label="Closed"
        checked={Boolean(form.closed)}
        onChange={(value) =>
          setForm((previous) => ({ ...previous, closed: value }))
        }
      />

      <div className="flex justify-end gap-2 md:col-span-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {submitting
            ? "Saving..."
            : initialValue
              ? "Update Leave Year"
              : "Create Leave Year"}
        </button>
      </div>
    </form>
  );
}

function Status({
  active,
  trueText,
  falseText,
}: {
  active: boolean;
  trueText: string;
  falseText: string;
}) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-200 text-slate-700"
      }`}
    >
      {active ? trueText : falseText}
    </span>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </div>
      {children}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border p-4 text-sm font-semibold text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4"
      />
      {label}
    </label>
  );
}